import React, { useState, useEffect, useRef } from "react";
import { FaPlay, FaPause, FaStop } from "react-icons/fa";
import sent from "../../assets/chatui/PaperPlaneRight.jpg";
import api from "../../api/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const VoiceRecorder = ({ isStartRecording, text, setTranscript, patient, fetchchat, setIsRecord }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState("00:00");
  const [savedTime, setSavedTime] = useState("00:00");
  const [audioUrl, setAudioUrl] = useState(null);
  const [loader, setLoader] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [blobFile, setBlobFile] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(null);
  const elapsedTimeRef = useRef(0);
  const shouldTranscribeRef = useRef(false); // Renamed for clarity

  const practiseid = window.getPracticeId();

  // 🟢 Start Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunks.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        // Only transcribe if shouldTranscribeRef is true
        if (!shouldTranscribeRef.current) {
          cleanupRecording();
          return;
        }

        const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        setBlobFile(audioBlob);

        setIsTranscribing(true);
        await handleTranscript(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setIsPaused(false);
      shouldTranscribeRef.current = true; // Reset flag when starting
      startTimeRef.current = Date.now();
      elapsedTimeRef.current = 0;
      updateRecordingTime();
    } catch (error) {
      console.error("Error accessing audio devices:", error);
    }
  };

  // Cleanup recording resources
  const cleanupRecording = () => {
    audioChunks.current = [];
    setBlobFile(null);
    setRecordingTime("00:00");
    setSavedTime("00:00");
    setIsRecord(false);
    setIsTranscribing(false);
    
    // Stop all media tracks
    if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
  };

  // 🔴 Stop Recording
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      shouldTranscribeRef.current = true; // Allow transcription
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  // ❌ Cancel Recording (no transcription)
  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      shouldTranscribeRef.current = false; // Prevent transcription
      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    }
    cleanupRecording();
  };

  // ⏸️ Pause Recording
  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      cancelAnimationFrame(animationFrameRef.current);
      elapsedTimeRef.current += Date.now() - startTimeRef.current;
      const minutes = Math.floor(elapsedTimeRef.current / 1000 / 60);
      const seconds = Math.floor((elapsedTimeRef.current / 1000) % 60);
      setSavedTime(`${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`);
    }
  };

  // ▶️ Resume Recording
  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimeRef.current = Date.now();
      updateRecordingTime();
    }
  };

  // ⏱️ Update Timer
  const updateRecordingTime = () => {
    const now = Date.now();
    const totalElapsedTime = elapsedTimeRef.current + (now - startTimeRef.current);
    const minutes = Math.floor(totalElapsedTime / 1000 / 60);
    const seconds = Math.floor((totalElapsedTime / 1000) % 60);
    setRecordingTime(`${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`);

    if (!isPaused) {
      animationFrameRef.current = requestAnimationFrame(updateRecordingTime);
    }
  };

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current) {
        shouldTranscribeRef.current = false;
        mediaRecorderRef.current.stop();
      }
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  useEffect(() => {
    if (isStartRecording) {
      startRecording();
    }
  }, [isStartRecording]);

  // 🧠 Transcription API
  const handleTranscript = async (blob) => {
    try {
      setLoader(true);
      const formData = new FormData();
      const randomString = `audio_${Math.random().toString(36).substring(2, 15)}.webm`;
      formData.append("audio_file", blob, randomString);

      const response = await api.post(
        `/v1/${practiseid}/messaging/speech_to_text`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setTranscript(response?.text || "");
    } catch (error) {
      console.error("❌ Error calling transcription API:", error);
      setTranscript("");
    } finally {
      cleanupRecording();
      setLoader(false);
    }
  };

  return (
    <div className="flex flex-col gap-[10px] w-full">
      {isRecording && !isTranscribing ? (
        <div className="w-full flex justify-center gap-4 items-center">
          <span style={{ color: isRecording ? "red" : "black" }}>    🎧 Listening
    <span className="dot-anim ml-1"></span>
</span>
          <span>{isPaused ? savedTime : recordingTime}</span>

          <button
            onClick={isPaused ? resumeRecording : pauseRecording}
            className="bg-gray-200 text-black border border-gray-400 rounded-lg px-2 py-2 shadow-md hover:bg-gray-300 hover:shadow-lg transition-all flex items-center justify-center"
          >
            {isPaused ? <FaPlay size={16} /> : <FaPause size={16} />}
          </button>

          <button
            onClick={stopRecording}
            className="bg-red-500 text-white border border-transparent rounded-lg px-2 py-2 shadow-md hover:scale-105 hover:shadow-lg transition-all flex items-center justify-center"
          >
            <FaStop size={16} />
          </button>

          <button
            className="bg-red-500 text-white px-2 py-2 rounded-lg shadow-md hover:scale-105 hover:shadow-lg transition-transform flex items-center justify-center"
            onClick={cancelRecording}
          >
            <FontAwesomeIcon icon={faXmark} className="text-lg" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between px-4 py-2 bg-white rounded-lg shadow-lg border border-gray-200">
          <span className="text-gray-800 font-semibold text-sm">
            {isTranscribing ? "🎧 Transcribing..." : "Ready to Send"}
          </span>

          {loader || isTranscribing ? (
            <div className="flex items-center justify-center w-8 h-8">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 100 100"
                width="24"
                height="24"
                className="animate-spin text-blue-500"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="#007bff"
                  strokeWidth="5"
                  fill="none"
                  strokeDasharray="283"
                  strokeDashoffset="75"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          ) : (
            <img
              onClick={() => fetchchat()}
              src={sent}
              alt="Send"
              className="cursor-pointer w-6 h-6 hover:scale-110 transition-transform"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;